import { useMemo, useRef, useState } from "react";
import { useGetAssetsQuery, useDeleteAssetMutation, useGetUsersQuery } from "../../redux/api";
import type { Asset } from "../../redux/api";
import { useImageUpload } from "../../hooks/useImageUpload";
import { useMediaUpload } from "../../hooks/useMediaUpload";
import { IconPlus, IconTrash, IconImage, IconVideo, IconEdit } from "../../components/icons";
import { ImagePreviewModal } from "../../components/ImagePreviewModal";
import { ImageEditorModal } from "../../components/ImageEditorModal";

const GALLERY_ENTITY_ID = "library";

const IMAGE_MIME_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

type TypeFilter = "all" | "image" | "video" | "unused";

const isVideoAsset = (a: Asset) =>
  a.mimeType.startsWith("video/") || a.mimeType === "application/vnd.apple.mpegurl";

// Nothing currently points at this file — typically a photo uploaded on a
// product/brand form that was then abandoned. Computed server-side from the
// owning records on every read, so it is never stale (see the Asset.usedBy
// resolver). Absent (rather than empty) means the caller didn't ask for it.
const isUnusedAsset = (a: Asset) => (a.usedBy?.length ?? 0) === 0;

const matchesFilter = (a: Asset, filter: TypeFilter) => {
  if (filter === "all") return true;
  if (filter === "unused") return isUnusedAsset(a);
  if (filter === "video") return isVideoAsset(a);
  return !isVideoAsset(a);
};

// "Used by Blue Widget" / "Used by 3 items" — enough to answer "is this safe
// to delete?" without opening anything.
const usageLabel = (a: Asset) => {
  const used = a.usedBy ?? [];
  if (used.length === 0) return "Unused";
  if (used.length === 1) return `Used by ${used[0].name ?? used[0].entityType.toLowerCase()}`;
  return `Used by ${used.length} items`;
};

const formatSize = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${Math.round(bytes / 1024)} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

// Settings → Gallery. Every image/video ever uploaded through Product/Brand/
// Organization (or straight from here) lands here automatically — see
// useImageUpload/useMediaUpload, both of which register a gallery Asset
// record right after a successful upload. This screen just lists/uploads/
// deletes them; the same grid + selection UI is reused by GalleryPickerModal
// wherever a form offers "choose from gallery" instead of uploading again.
const Gallery = () => {
  const { data: assets = [], isLoading, error, refetch } = useGetAssetsQuery();
  const [deleteAsset, { isLoading: deleting }] = useDeleteAssetMutation();

  const { data: users = [] } = useGetUsersQuery();
  const nameByEmail = useMemo(
    () =>
      Object.fromEntries(
        users.map((u) => [
          u.email.toLowerCase(),
          `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.email,
        ]),
      ),
    [users],
  );
  const uploaderName = (email?: string | null) =>
    email ? nameByEmail[email.toLowerCase()] ?? email : null;

  const { upload: uploadImage, uploading: uploadingImage, error: imageError } = useImageUpload(
    "GALLERY",
    GALLERY_ENTITY_ID,
  );
  const { upload: uploadMedia, uploading: uploadingMedia, error: mediaError } = useMediaUpload(
    "GALLERY",
    GALLERY_ENTITY_ID,
  );

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [filter, setFilter] = useState<TypeFilter>("all");
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [previewAsset, setPreviewAsset] = useState<Asset | null>(null);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);

  const uploading = uploadingImage || uploadingMedia;

  const items = useMemo(() => assets.filter((a) => matchesFilter(a, filter)), [assets, filter]);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setUploadError(null);
    const uploaded = IMAGE_MIME_TYPES.includes(file.type)
      ? await uploadImage(file)
      : await uploadMedia(file);
    if (!uploaded) {
      setUploadError(imageError ?? mediaError ?? "Failed to upload file.");
    }
  };

  const handleDelete = async (asset: Asset) => {
    const used = asset.usedBy ?? [];
    // Deleting only removes the gallery listing — the S3 object and any
    // product/brand still pointing at it are untouched — but the file then
    // becomes unreachable from the pickers, so say what it is attached to
    // rather than asking a bare yes/no.
    const message = used.length
      ? `"${asset.fileName}" is still used by ${used
          .map((u) => u.name ?? u.entityType.toLowerCase())
          .join(", ")}. Remove it from the gallery anyway?`
      : `Remove "${asset.fileName}" from the gallery?`;

    if (!window.confirm(message)) return;
    try {
      await deleteAsset(asset.id).unwrap();
    } catch (err) {
      window.alert((err as { message?: string })?.message ?? "Failed to delete asset.");
    }
  };

  const errorMessage = error
    ? (error as { message?: string }).message ?? "Failed to load gallery"
    : null;

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Gallery</h2>
            <p className="panel__sub">{items.length} file(s)</p>
          </div>
          <div className="toolbar">
            <select
              className="select"
              value={filter}
              onChange={(e) => setFilter(e.target.value as TypeFilter)}
            >
              <option value="all">All</option>
              <option value="image">Images</option>
              <option value="video">Videos</option>
              <option value="unused">Unused</option>
            </select>
            <button type="button" className="btn" onClick={() => refetch()} disabled={isLoading}>
              {isLoading ? "Refreshing…" : "Refresh"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,video/mp4,video/quicktime,video/webm"
              style={{ display: "none" }}
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <IconPlus /> {uploading ? "Uploading…" : "Upload"}
            </button>
          </div>
        </div>

        <div className="panel__body">
          {uploadError ? <div className="form-error">{uploadError}</div> : null}

          {errorMessage ? (
            <div className="table-error">
              <span>{errorMessage}</span>
              <button type="button" className="btn btn--ghost" onClick={() => refetch()}>
                Try again
              </button>
            </div>
          ) : isLoading ? (
            <p className="panel__sub">Loading…</p>
          ) : items.length === 0 ? (
            <div className="gallery-empty">
              No files yet — upload an image or video to get started.
            </div>
          ) : (
            <div className="gallery-grid">
              {items.map((asset) => (
                <div key={asset.id} className="gallery-item gallery-item--selectable" onClick={() => setPreviewAsset(asset)}>
                  <div className="gallery-item__thumb">
                    {isVideoAsset(asset) ? (
                      <IconVideo size={28} />
                    ) : asset.url ? (
                      <img src={asset.url} alt={asset.fileName} />
                    ) : (
                      <IconImage size={28} />
                    )}
                  </div>
                  <div className="gallery-item__meta">
                    <span className="gallery-item__name" title={asset.fileName}>
                      {asset.fileName}
                    </span>
                    <span className="gallery-item__sub">
                      {formatSize(asset.fileSize)} · {new Date(asset.uploadedAt).toLocaleDateString()}
                    </span>
                    {uploaderName(asset.uploadedBy) ? (
                      <span className="gallery-item__sub">Uploaded by {uploaderName(asset.uploadedBy)}</span>
                    ) : null}
                    {/* Unused is the state worth noticing (it's the one you can
                        safely clear out), so it gets the accent; "used by ..."
                        is reassurance and stays quiet. */}
                    <span
                      className="gallery-item__sub"
                      style={{ fontWeight: 600, color: isUnusedAsset(asset) ? "var(--warning)" : undefined }}
                    >
                      {usageLabel(asset)}
                    </span>
                  </div>
                  <div className="gallery-item__actions">
                    {/* Any image in the library can be edited, however long
                        ago it was uploaded — the editor works from the stored
                        key, so nothing needs re-uploading. Video has no
                        equivalent, hence images only. */}
                    {!isVideoAsset(asset) ? (
                      <button
                        type="button"
                        className="icon-btn"
                        aria-label="Edit image"
                        title="Crop, rotate and adjust"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingAsset(asset);
                        }}
                      >
                        <IconEdit />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label="Delete asset"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(asset);
                      }}
                      disabled={deleting}
                    >
                      <IconTrash />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <ImagePreviewModal
        open={!!previewAsset}
        onClose={() => setPreviewAsset(null)}
        url={previewAsset?.url}
        fileName={previewAsset?.fileName}
        isVideo={previewAsset ? isVideoAsset(previewAsset) : false}
      />

      {/* The edit lands as its own new asset (the original is left alone), and
          registering it invalidates the Asset list — so the grid picks it up
          without anything to do here. */}
      <ImageEditorModal
        open={!!editingAsset}
        onClose={() => setEditingAsset(null)}
        imageKey={editingAsset?.key ?? null}
        url={editingAsset?.url ?? null}
        fileName={editingAsset?.fileName ?? ""}
        onApplied={() => setEditingAsset(null)}
      />
    </div>
  );
};

export default Gallery;
